/**
 * Locations state — mirrors `content/hooks.ts`'s `useManagedContentList` pattern: a mutation
 * applies the row the API returned to what is already on screen, then reconciles against a fresh
 * read in the background. No page reload, ever.
 *
 * Three shapes here, matching the three surfaces `api.ts` exposes:
 *
 * - `useManagedLocationsList` — the existing narrow list, now with `remove` alongside the read, the
 *   same optimistic-apply-then-reconcile behaviour `useManagedContentList` gives content. Create has
 *   no place in this hook: `LocationCreateForm.tsx` posts directly and navigates to the new
 *   location's own detail route rather than back to this list, the same reasoning
 *   `ContentFormPage.tsx` gives for not routing its own create through `useManagedContentList`.
 * - `useManagedLocation` — one location's wide detail, with its own `update`.
 * - `useManagedMaquinas`/`useManagedOtcUsers` — the two nested CRUD lists, scoped to whichever
 *   location is open. Both are the same shape as `useManagedContentList`, parameterised over the
 *   locationId instead of a content type.
 */
import type {
  AdminLocationList,
  CreateMaquina,
  CreateOtcUser,
  DeleteResult,
  LocationDetail,
  Maquina,
  OtcUser,
  UpdateLocation,
  UpdateMaquina,
  UpdateOtcUser,
} from '@apuestapr/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';
import {
  accessFor,
  useRequest,
  type MutationState,
  type RequestState,
} from '../api/request-state.ts';
import { createLocationsApi } from './api.ts';

/** Kept for any caller that only reads the narrow list and does not need create/delete. */
export const useLocationsList = (
  client: ApiClient,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<AdminLocationList> => {
  const api = useMemo(() => createLocationsApi(client), [client]);
  const load = useCallback(() => api.list(), [api]);
  return useRequest(load, onFailure);
};

export interface ManagedLocationsList {
  readonly state: RequestState<AdminLocationList>;
  readonly mutation: MutationState<DeleteResult>;
  readonly reconciling: boolean;
  readonly remove: (id: number) => Promise<ApiResult<DeleteResult>>;
  readonly reconcile: () => Promise<void>;
}

/**
 * The narrow list, with the same optimistic-apply-then-reconcile mutation behaviour the nested row
 * lists below already have — just the one mutation, `remove`, since create does not go through
 * this hook (see the module comment above).
 *
 * No `locationId`-style key here, unlike `useManagedLocationRowList` below — this hook has exactly
 * one instance for the lifetime of the list screen, so the generation counter that hook needs to
 * discard a stale in-flight request for a *previous* key has nothing to protect against here. A
 * `mounted` ref is still worth keeping: `remove` awaits the network, and the screen can navigate
 * away — to a location's own detail route on success, or elsewhere — before that resolves.
 */
export const useManagedLocationsList = (
  client: ApiClient,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedLocationsList => {
  const api = useMemo(() => createLocationsApi(client), [client]);
  const [state, setState] = useState<RequestState<AdminLocationList>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<DeleteResult>>({ status: 'ready' });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    setReconciling(true);
    const result = await api.list();
    if (!mounted.current) return;
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [api, onFailure]);

  useEffect(() => {
    void api.list().then((result) => {
      if (!mounted.current) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
  }, [api, onFailure]);

  const remove = useCallback(
    async (id: number): Promise<ApiResult<DeleteResult>> => {
      setMutation({ status: 'loading' });
      const result = await api.deleteLocation(id, csrfToken);
      if (!mounted.current) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', data: current.data.filter((row) => row.id !== id) }
          : current,
      );
      setMutation({ status: 'ready', data: result.data });
      void reconcile();
      return result;
    },
    [api, csrfToken, onFailure, reconcile],
  );

  return { state, mutation, reconciling, remove, reconcile };
};

export interface ManagedLocation {
  readonly state: RequestState<LocationDetail>;
  readonly mutation: MutationState<LocationDetail>;
  readonly reconciling: boolean;
  readonly update: (input: UpdateLocation) => Promise<ApiResult<LocationDetail>>;
  readonly reconcile: () => Promise<void>;
}

export const useManagedLocation = (
  client: ApiClient,
  locationId: number,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedLocation => {
  const api = useMemo(() => createLocationsApi(client), [client]);
  const [state, setState] = useState<RequestState<LocationDetail>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<LocationDetail>>({ status: 'ready' });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const activeId = useRef(locationId);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    const current = generation.current + 1;
    generation.current = current;
    setReconciling(true);
    const result = await api.detail(locationId);
    if (!mounted.current || activeId.current !== locationId || current !== generation.current) {
      return;
    }
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [api, locationId, onFailure]);

  useEffect(() => {
    let active = true;
    activeId.current = locationId;
    generation.current += 1;
    queueMicrotask(() => {
      if (active) {
        setState({ status: 'loading' });
        setMutation({ status: 'ready' });
        setReconciling(false);
      }
    });
    void api.detail(locationId).then((result) => {
      if (!active) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
    return () => {
      active = false;
    };
  }, [api, locationId, onFailure]);

  const update = useCallback(
    async (input: UpdateLocation): Promise<ApiResult<LocationDetail>> => {
      setMutation({ status: 'loading' });
      const result = await api.update(locationId, input, csrfToken);
      if (!mounted.current || activeId.current !== locationId) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }
      setState({ status: 'ready', data: result.data });
      setMutation({ status: 'ready', data: result.data });
      void reconcile();
      return result;
    },
    [api, csrfToken, locationId, onFailure, reconcile],
  );

  return { state, mutation, reconciling, update, reconcile };
};

export interface ManagedRowList<Row extends { readonly id: number }, Create, Update> {
  readonly state: RequestState<readonly Row[]>;
  readonly mutation: MutationState<Row | DeleteResult>;
  readonly reconciling: boolean;
  readonly create: (input: Create) => Promise<ApiResult<Row>>;
  readonly update: (id: number, input: Update) => Promise<ApiResult<Row>>;
  readonly remove: (id: number) => Promise<ApiResult<DeleteResult>>;
  readonly reconcile: () => Promise<void>;
}

/**
 * The shared shape behind `useManagedMaquinas` and `useManagedOtcUsers`: a locationId-scoped list
 * with the same optimistic-apply-then-reconcile mutation behaviour `useManagedContentList` gives
 * content. One generic function rather than two near-identical hooks, since the two nested
 * resources differ only in which three endpoint calls they make.
 */
const useManagedLocationRowList = <Row extends { readonly id: number }, Create, Update>(
  locationId: number,
  list: () => Promise<ApiResult<readonly Row[]>>,
  createRow: (input: Create) => Promise<ApiResult<Row>>,
  updateRow: (id: number, input: Update) => Promise<ApiResult<Row>>,
  removeRow: (id: number) => Promise<ApiResult<DeleteResult>>,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedRowList<Row, Create, Update> => {
  const [state, setState] = useState<RequestState<readonly Row[]>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<Row | DeleteResult>>({ status: 'ready' });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);
  const generation = useRef(0);
  const activeId = useRef(locationId);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    const current = generation.current + 1;
    generation.current = current;
    setReconciling(true);
    const result = await list();
    if (!mounted.current || activeId.current !== locationId || current !== generation.current) {
      return;
    }
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [list, locationId, onFailure]);

  useEffect(() => {
    let active = true;
    activeId.current = locationId;
    generation.current += 1;
    queueMicrotask(() => {
      if (active) {
        setState({ status: 'loading' });
        setMutation({ status: 'ready' });
        setReconciling(false);
      }
    });
    void list().then((result) => {
      if (!active) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
    return () => {
      active = false;
    };
    // `list` is a fresh closure per render from the caller; keying on `locationId` alone is
    // deliberate, the same way `useManagedContentList` keys on `type` and not on `api`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, onFailure]);

  const runMutation = useCallback(
    async <Value>(
      operation: () => Promise<ApiResult<Value>>,
      apply: (rows: readonly Row[], value: Value) => readonly Row[],
    ): Promise<ApiResult<Value>> => {
      setMutation({ status: 'loading' });
      const result = await operation();
      if (!mounted.current || activeId.current !== locationId) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', data: apply(current.data, result.data) }
          : current,
      );
      setMutation({ status: 'ready', data: result.data as Row | DeleteResult });
      void reconcile();
      return result;
    },
    [locationId, onFailure, reconcile],
  );

  const create = useCallback(
    (input: Create) =>
      runMutation(
        () => createRow(input),
        (rows, row) => [...rows, row],
      ),
    [createRow, runMutation],
  );
  const update = useCallback(
    (id: number, input: Update) =>
      runMutation(
        () => updateRow(id, input),
        (rows, row) => rows.map((current) => (current.id === row.id ? row : current)),
      ),
    [runMutation, updateRow],
  );
  const remove = useCallback(
    (id: number) =>
      runMutation(
        () => removeRow(id),
        (rows) => rows.filter((row) => row.id !== id),
      ),
    [removeRow, runMutation],
  );

  return { state, mutation, reconciling, create, update, remove, reconcile };
};

export const useManagedMaquinas = (
  client: ApiClient,
  locationId: number,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedRowList<Maquina, CreateMaquina, UpdateMaquina> => {
  const api = useMemo(() => createLocationsApi(client), [client]);
  return useManagedLocationRowList<Maquina, CreateMaquina, UpdateMaquina>(
    locationId,
    useCallback(() => api.listMaquinas(locationId), [api, locationId]),
    useCallback(
      (input: CreateMaquina) => api.createMaquina(locationId, input, csrfToken),
      [api, csrfToken, locationId],
    ),
    useCallback(
      (id: number, input: UpdateMaquina) => api.updateMaquina(locationId, id, input, csrfToken),
      [api, csrfToken, locationId],
    ),
    useCallback(
      (id: number) => api.deleteMaquina(locationId, id, csrfToken),
      [api, csrfToken, locationId],
    ),
    onFailure,
  );
};

export const useManagedOtcUsers = (
  client: ApiClient,
  locationId: number,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedRowList<OtcUser, CreateOtcUser, UpdateOtcUser> => {
  const api = useMemo(() => createLocationsApi(client), [client]);
  return useManagedLocationRowList<OtcUser, CreateOtcUser, UpdateOtcUser>(
    locationId,
    useCallback(() => api.listOtcUsers(locationId), [api, locationId]),
    useCallback(
      (input: CreateOtcUser) => api.createOtcUser(locationId, input, csrfToken),
      [api, csrfToken, locationId],
    ),
    useCallback(
      (id: number, input: UpdateOtcUser) => api.updateOtcUser(locationId, id, input, csrfToken),
      [api, csrfToken, locationId],
    ),
    useCallback(
      (id: number) => api.deleteOtcUser(locationId, id, csrfToken),
      [api, csrfToken, locationId],
    ),
    onFailure,
  );
};
